/**
 * The HMIS report to DHIS2: a mapping file turns each computed value ("ELEMENT|key") into a DHIS2
 * data element and category option combo, and the values go as a dataValueSet to the facility's
 * org unit. Shared by the ASSIST server and the EMR's DHIS2 relay (emr/dhis2-relay). The national
 * eHMIS uses the Ethiopian calendar: the period is the HMIS month's id ("201902" = Tikimt 2019).
 */
import { z } from 'zod';
const UID = z.string().regex(/^[A-Za-z][A-Za-z0-9]{10}$/, 'not a DHIS2 UID');
export const zMapping = z.object({
    dataSet: UID.optional(),
    attributeOptionCombo: UID.optional(),
    values: z.record(z.object({ dataElement: UID, categoryOptionCombo: UID.optional() })),
});
/** The DHIS2 payload for a report, and the computed values the mapping does not cover. */
export function toDataValues(report, mapping, orgUnit) {
    const dataValues = [];
    const unmapped = [];
    for (const el of report.elements)
        for (const v of el.values) {
            const key = `${v.element}|${v.key}`;
            const m = mapping.values[key];
            if (!m) {
                unmapped.push(key);
                continue;
            }
            dataValues.push({
                dataElement: m.dataElement,
                ...(m.categoryOptionCombo ? { categoryOptionCombo: m.categoryOptionCombo } : {}),
                ...(mapping.attributeOptionCombo
                    ? { attributeOptionCombo: mapping.attributeOptionCombo }
                    : {}),
                orgUnit,
                period: report.period.id,
                value: String(v.value),
            });
        }
    return { dataValues, unmapped };
}
/** POST a dataValueSet. Understands both the old (importSummary) and new (WebMessage) replies. */
export async function pushDataValues(cfg, dataValues, fetchImpl = fetch, dataSet) {
    const res = await fetchImpl(`${cfg.url}/api/dataValueSets?dryRun=${cfg.dryRun}&importStrategy=CREATE_AND_UPDATE`, {
        method: 'POST',
        headers: {
            authorization: cfg.auth,
            'content-type': 'application/json',
            accept: 'application/json',
        },
        body: JSON.stringify(dataSet ? { dataSet, dataValues } : { dataValues }),
        signal: AbortSignal.timeout(60_000),
    });
    const body = (await res.json().catch(() => ({})));
    if (res.status === 401 || res.status === 403)
        throw new Error(`DHIS2 refused the credentials (${res.status})`);
    const summary = body.response ?? body;
    const counts = summary.importCount ?? {};
    const status = summary.status ?? body.status ?? (res.ok ? 'OK' : `HTTP ${res.status}`);
    return {
        ok: res.ok && status !== 'ERROR',
        dryRun: cfg.dryRun,
        status,
        imported: counts.imported ?? 0,
        updated: counts.updated ?? 0,
        ignored: counts.ignored ?? 0,
        conflicts: (summary.conflicts ?? []).slice(0, 50),
    };
}
/** A whole dataValueSet: what is sent, and what a file for DHIS2's import app holds. */
export function dataValueSet(report, mapping, orgUnit) {
    const { dataValues, unmapped } = toDataValues(report, mapping, orgUnit);
    return {
        set: {
            ...(mapping.dataSet ? { dataSet: mapping.dataSet } : {}),
            period: report.period.id,
            orgUnit,
            dataValues,
        },
        unmapped,
    };
}
//# sourceMappingURL=dhis2.js.map